// Edits of a version's requirements list (docs/12 "Requirements", D55). Task groups bind
// credential requirements by index, so edits keep the order of what they do not touch. VM
// isolation is a toggle: at most one isolation entry, kept last; so is each toolchain (D90).
import { CREDENTIAL_CHOICES, type CredentialChoice, type CredentialRequirement, type Requirement, TOOLCHAINS, type Toolchain } from "../../catalog/types";
import { messages } from "../../i18n";

export const isModelKey = (kind: CredentialChoice): boolean => kind.startsWith("anthropic");

/** A requirement in words, as the versions list and a task group's requirements show it. */
export function requirementLabel(r: Requirement): string {
  const t = messages().workflows.needs.requirements;
  switch (r.kind) {
    case "credential": {
      const known: Partial<Record<string, string>> = t.credential;
      const label = known[r.credential_kind] ?? r.credential_kind;
      return r.required ? label : t.optional(label);
    }
    case "oauth":
      return t.oauth(r.provider);
    case "isolation":
      return t.dedicatedVm;
    case "toolchain":
      return t.toolchain(t.toolchainNames[r.toolchain] ?? r.toolchain);
  }
}

const credentialKinds = (list: Requirement[]) => list.flatMap((r) => (r.kind === "credential" ? [r.credential_kind] : []));

/** A new credential requirement of a kind not yet listed (`anthropic` and its kinds overlap). */
export function addCredential(list: Requirement[]): Requirement[] {
  const used = credentialKinds(list);
  const overlaps = (k: CredentialChoice) => used.some((u) => u === k || (isModelKey(u) && isModelKey(k) && (u === "anthropic" || k === "anthropic")));
  const kind = CREDENTIAL_CHOICES.find((k) => !overlaps(k)) ?? "generic";
  const added: CredentialRequirement = { kind: "credential", credential_kind: kind, required: true };
  return insertBeforeIsolation(list, added);
}

export function addOauth(list: Requirement[]): Requirement[] {
  return insertBeforeIsolation(list, { kind: "oauth", provider: "", required: false });
}

function insertBeforeIsolation(list: Requirement[], added: Requirement): Requirement[] {
  const at = list.findIndex((r) => r.kind === "isolation");
  return at < 0 ? [...list, added] : [...list.slice(0, at), added, ...list.slice(at)];
}

export const requiresVm = (list: Requirement[]): boolean => list.some((r) => r.kind === "isolation");

/** The toolchains the version requires (D90), in table order. */
export const requiredToolchains = (list: Requirement[]): Toolchain[] =>
  TOOLCHAINS.filter((t) => list.some((r) => r.kind === "toolchain" && r.toolchain === t));

/** A toolchain requirement on or off; a new one goes before isolation, like other requirements. */
export function setToolchain(list: Requirement[], toolchain: Toolchain, on: boolean): Requirement[] {
  const rest = list.filter((r) => !(r.kind === "toolchain" && r.toolchain === toolchain));
  return on ? insertBeforeIsolation(rest, { kind: "toolchain", toolchain }) : rest;
}

export function setVm(list: Requirement[], on: boolean): Requirement[] {
  const rest = list.filter((r) => r.kind !== "isolation");
  return on ? [...rest, { kind: "isolation", level: "vm" }] : rest;
}

export function replace(list: Requirement[], index: number, next: Requirement): Requirement[] {
  return list.map((r, i) => (i === index ? next : r));
}

export function remove(list: Requirement[], index: number): Requirement[] {
  return list.filter((_, i) => i !== index);
}

/** Changing the kind drops a platform default the new kind cannot have. */
export function withKind(req: CredentialRequirement, kind: CredentialChoice): CredentialRequirement {
  const { platform_default, ...rest } = req;
  return isModelKey(kind) && platform_default ? { ...rest, credential_kind: kind, platform_default } : { ...rest, credential_kind: kind };
}

/** A name a pre-execution script's grant can use (D77); empty removes it. Lower-cased as typed. */
export function withName(req: CredentialRequirement, name: string): CredentialRequirement {
  const { name: _, ...rest } = req;
  const typed = name.trim().toLowerCase();
  return typed ? { ...rest, name: typed } : rest;
}
